import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import SomisLogo from '../Header/SomisLogo';
import { theme, width } from '../../styles/theme';
import { PeikkoContext } from '../../contexts/PeikkoContext';
import { SmallNavMenuButton, SmallestNavMenuButton } from './NavMenuButton';
import { getLegalPageLink, getSeparateLegalLinks, countries } from '../i18n';
import { DisableBodyScroll } from '../utils';
import {
  resetCookiesConsent,
  isCookiesConsentGiven,
  getToken,
} from '../../utils/cookie';
import Modal from '../Modal';
import { Animations } from '../Avatar/AvatarAnimations';
import { requestAccountDeletion } from '../../apis/IdentifyApi';
import { ACCOUNT_DELETION_ARTICLE } from '../../utils/constants';
import Analytics from '../../utils/analytics';
import { logger } from '../../utils/logger';
import { getCountryFromLocale } from '../../utils/helpers';

// Use SSR-provided locale to avoid hydration mismatch
const showLocaleSelector = (user: User, ssrLocale: string) => {
  const locale = user?.locale || ssrLocale || 'NO_LOCALE';
  const countryNameCode = getCountryFromLocale(locale);

  if (!countryNameCode) return false;
  if (countryNameCode === 'PRO') return false;
  const country = countries.find((c) => c.shortname === countryNameCode);
  return country && country.languages && country.languages.length > 1;
};

// SmallestNavMenuButton sets `padding: 5px 0` because its only other use is
// nested inside SmallNavMenuButton, which already supplies the 20px gutter.
// Used standalone it sits flush against the panel edge, visibly out of line
// with every other row — so these status messages carry the gutter themselves.
const StatusRow = styled(SmallestNavMenuButton)`
  padding: 10px 20px;
`;

const styledNavMenutProps = {
  isOpen: false,
};

type StyledNavMenutProps = Readonly<typeof styledNavMenutProps>;

type StyledNavMenuProps = Partial<StyledNavMenutProps>;

const StyledNavMenu = styled.div.attrs<{ $isOpen: boolean }>((props) => ({
  $isOpen: props.$isOpen,
}))<StyledNavMenuProps>`
  box-sizing: border-box;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  height: 100%;
  background-color: ${theme.palette.secondary};
  transition: 100ms linear;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  overflow-y: scroll;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const NavMenuWrapper = styled.div`
  max-width: ${width.container}px;
  width: 100%;
  margin: 0 auto;
`;

const LogoContainer = styled.div`
  max-width: 80px;
`;

const DividerLine = styled.div`
  background-color: rgba(255, 255, 255, 0.14);
  height: 1px;
  width: 100%;
`;

const DividerContainer = styled.div`
  height: 1px;
  width: 100%;
  padding: 0 20px;
  margin: 25px 0;
`;

const Divider = () => (
  <DividerContainer>
    <DividerLine />
  </DividerContainer>
);

// Imported dynamically so that Crisp only gets rendered client-side
// Using a null loading placeholder to ensure consistent server/client rendering
const HelpButton = dynamic(() => import('./HelpButton'), {
  ssr: false,
  loading: () => null,
});

type CloseButtonLineProps = {
  rotate: number;
};

const CloseButtonLine = styled.span<CloseButtonLineProps>`
  top: 0;
  width: 2px;
  height: 20px;
  background-color: ${theme.palette.background};
  transform: rotate(${({ rotate }) => rotate}deg);
  position: absolute;
`;

const CloseButtonContainer = styled.button`
  background-color: transparent;
  border: none;
  position: relative;
  width: 20px;
  height: 20px;
`;

const CloseButton = (props) => (
  <CloseButtonContainer {...props}>
    <CloseButtonLine rotate={45} />
    <CloseButtonLine rotate={315} />
  </CloseButtonContainer>
);

const ButtonContainer = styled.div`
  width: 100%;
`;

const TopRowContainer = styled.div`
  padding: 25px 25px 50px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

interface NavMenuProps {
  isOpen: boolean;
  closeMenu: () => void;
  user: User;
  token?: string;
  isNativeShell?: boolean;
  gdprConsentGiven?: string;
}

const NavMenu = ({
  isOpen,
  closeMenu,
  user,
  token,
  isNativeShell,
  gdprConsentGiven,
}: NavMenuProps): React.ReactElement => {
  const { i18n } = useContext(PeikkoContext);
  const router = useRouter();

  const { locale } = i18n;

  const goToCookieSettings = () => {
    resetCookiesConsent();
    router.push('/cookies/settings');
    closeMenu();
  };

  const goToLocaleSettings = () => {
    router.push('/locale');
    closeMenu();
  };

  // Use SSR-provided cookie consent value to avoid hydration mismatch
  const showCookieSettings = isCookiesConsentGiven(gdprConsentGiven);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletionState, setDeletionState] = useState<
    'idle' | 'sending' | 'done' | 'error'
  >('idle');

  // Two conditions, and both matter.
  //
  // TOKEN, not `user`: several routes reach /voucher with no identify user at
  // all — the 404 branch in _app.tsx returns pageProps without one, and its own
  // comment says the page "needs only host+token, no user object on a 404". A
  // user object only exists once identify has a subscription row, which is
  // precisely what these people do not have. The token is also what proves who
  // they are and what the request is sent with.
  //
  // NATIVE SHELL: matches st-toosa, where the deletion entry is shell-only
  // because the store rules attach to the apps rather than the website, and web
  // users keep the erasure route via the helpdesk. Without this, peikko offered
  // in-app deletion on the web while toosa deliberately did not — an
  // inconsistency, and unreviewed copy on a surface nobody had signed off.
  //
  // Both come from pageProps, so SSR and the client agree.
  const showDeleteAccount = Boolean(token) && Boolean(isNativeShell);

  const handleDeleteAccount = async () => {
    if (deletionState === 'sending') return;
    setOpenDeleteModal(false);
    setDeletionState('sending');
    try {
      // Identity comes from the token: jakari scopes to `req.auth.sub` and
      // ignores anything the client sends, so there is nothing to pass here.
      await requestAccountDeletion(token || getToken(), window.location.host);
      Analytics.logClick('navmenu_delete_account_requested');
      setDeletionState('done');
    } catch (error) {
      // Never report success for a request that did not land — the fallback
      // article link below is the user's route when our backend cannot take it.
      logger.error('Failed to request account deletion', error as Error);
      setDeletionState('error');
    }
  };

  const separateLegalLinks = getSeparateLegalLinks(locale);
  const legalLinks = separateLegalLinks
    ? [
        {
          key: 'terms_of_service',
          href: separateLegalLinks.termsOfService,
          label: 'nav_menu.terms_of_service',
        },
        {
          key: 'privacy_policy',
          href: separateLegalLinks.privacyPolicy,
          label: 'nav_menu.privacy_policy',
        },
      ]
    : [
        {
          key: 'legal_page',
          href: getLegalPageLink(locale),
          label: 'nav_menu.termsAndProtection',
        },
      ];

  return (
    <>
      {isOpen ? <DisableBodyScroll /> : null}
      <StyledNavMenu $isOpen={isOpen}>
        <NavMenuWrapper>
          <ButtonContainer>
            <TopRowContainer>
              <LogoContainer>
                <SomisLogo fill={theme.palette.background} />
              </LogoContainer>
              <CloseButton onClick={closeMenu} />
            </TopRowContainer>
            <HelpButton i18n={i18n} locale={locale} onClick={closeMenu} />
          </ButtonContainer>
          <Divider />
          <ButtonContainer>
            {showLocaleSelector(user, locale) && (
              <SmallNavMenuButton onClick={goToLocaleSettings}>
                {i18n.t('nav_menu.changeLanguage')}
                <br />
                <SmallestNavMenuButton>
                  {i18n.t('nav_menu.changeLanguageOther')}
                </SmallestNavMenuButton>
              </SmallNavMenuButton>
            )}
            {legalLinks.map(({ key, href, label }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SmallNavMenuButton>{i18n.t(label)}</SmallNavMenuButton>
              </a>
            ))}
          </ButtonContainer>
          {showCookieSettings && (
            <>
              <Divider />
              <ButtonContainer>
                <SmallNavMenuButton onClick={goToCookieSettings}>
                  {i18n.t('nav_menu.cookieSettings')}
                </SmallNavMenuButton>
              </ButtonContainer>
            </>
          )}
          {showDeleteAccount && (
            <>
              <Divider />
              <ButtonContainer>
                {deletionState === 'done' ? (
                  <StatusRow data-testid="deletion-requested">
                    {i18n.t('nav_menu.deleteRequested')}
                  </StatusRow>
                ) : (
                  <SmallNavMenuButton
                    data-testid="delete-account"
                    onClick={() => {
                      Analytics.logClick('navmenu_delete_account');
                      setOpenDeleteModal(true);
                    }}
                  >
                    {i18n.t('nav_menu.deleteAccount')}
                  </SmallNavMenuButton>
                )}
                {deletionState === 'error' && (
                  <StatusRow data-testid="deletion-error">
                    {i18n.t('nav_menu.deleteError')}{' '}
                    {/* The helpdesk article is the fallback, not the primary
                        route: it works when our own backend cannot take the
                        request. Opened in the system browser, since these hosts
                        sit outside kuori's allowNavigation. */}
                    <a
                      href={
                        ACCOUNT_DELETION_ARTICLE[locale] ||
                        ACCOUNT_DELETION_ARTICLE.default
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {i18n.t('nav_menu.deleteAccount')}
                    </a>
                  </StatusRow>
                )}
              </ButtonContainer>
            </>
          )}
        </NavMenuWrapper>
      </StyledNavMenu>
      <Modal
        animation={Animations.smile0}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        title={() => <span>{i18n.t('nav_menu.deleteConfirmTitle')}</span>}
        description={i18n.t('nav_menu.deleteConfirmDescription')}
        cancelButtonText={i18n.t('nav_menu.deleteConfirmCancel')}
        cancelButtonOnClick={() => setOpenDeleteModal(false)}
        acceptButtonText={i18n.t('nav_menu.deleteConfirmAccept')}
        acceptButtonOnClick={handleDeleteAccount}
      />
    </>
  );
};

export default NavMenu;
